import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
} from '@mui/material';
import { ArrowLeft, ArrowRight, Receipt, Save } from 'lucide-react';
import { vouchersApi } from '../../../lib/api';
import { useAppStore } from '../../../lib/store';
import { isRTL } from '../../../lib/i18n';

export default function VoucherCreate() {
  const { t } = useTranslation(['finance', 'common']);
  const navigate = useNavigate();
  const language = useAppStore((state) => state.settings.language);
  const user = useAppStore((state) => state.user);

  // Note: vouchers schema only has: id, voucher_type, voucher_number, amount, currency, status, description,
  //       property_id, tenant_id, created_by, created_at, updated_at
  // payment_method does NOT exist in the vouchers table
  const [formData, setFormData] = useState({
    voucher_type: 'receipt' as 'receipt' | 'payment' | 'journal',
    voucher_number: '',
    amount: '',
    currency: 'SAR',
    status: 'draft' as 'draft' | 'posted' | 'cancelled',
    description: '',
  });

  // VAT settings
  const [includeVat, setIncludeVat] = useState(true);
  const [vatRate, setVatRate] = useState(15);

  // Calculate VAT and totals
  const baseAmount = Number(formData.amount) || 0;
  const vatAmount = includeVat ? (baseAmount * vatRate) / 100 : 0;
  const totalAmount = baseAmount + vatAmount;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: formData.currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    generateVoucherNumber();
  }, [formData.voucher_type]);

  const generateVoucherNumber = () => {
    const prefixes = {
      receipt: 'RCV',
      payment: 'PAY',
      journal: 'JRN',
    };
    const prefix = prefixes[formData.voucher_type];
    const year = new Date().getFullYear();
    const timestamp = Date.now().toString().slice(-6);
    const voucherNumber = `${prefix}-${year}-${timestamp}`;
    setFormData((prev) => ({ ...prev, voucher_number: voucherNumber }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.voucher_number.trim()) {
      newErrors.voucher_number = t('common:required');
    }

    if (!formData.amount || isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      newErrors.amount = t('common:required');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('common:required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    if (!user) {
      setError(t('common:auth.sessionExpired'));
      return;
    }

    setLoading(true);
    try {
      // Note: vouchers schema only has: id, voucher_type, voucher_number, amount, currency, status, description,
      //       property_id, tenant_id, created_by, created_at, updated_at
      // payment_method does NOT exist in the vouchers table
      // Store total amount (including VAT if applicable), and include VAT details in description
      const vatInfo = includeVat && vatRate > 0
        ? `\n[VAT: ${vatRate}% = ${formatCurrency(vatAmount)}]`
        : '';
      const voucherData = {
        voucher_type: formData.voucher_type,
        voucher_number: formData.voucher_number.trim(),
        amount: totalAmount, // Store total including VAT
        currency: formData.currency,
        status: formData.status,
        description: formData.description.trim() + vatInfo,
        created_by: user.id,
      };

      const response = await vouchersApi.create(voucherData);

      if (response.error) {
        throw new Error(response.error.message || t('common:somethingWentWrong'));
      }

      // Success - navigate back to list
      navigate('/dashboard/finance/vouchers');
    } catch (error: any) {
      console.error('Error creating voucher:', error);
      setError(error.message || t('common:tryAgain'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box dir={language === 'ar' ? 'rtl' : 'ltr'} sx={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          {...(isRTL()
            ? { endIcon: <ArrowRight size={20} /> }
            : { startIcon: <ArrowLeft size={20} /> }
          )}
          onClick={() => navigate('/dashboard/finance/vouchers')}
        >
          {t('common:back')}
        </Button>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {t('addVoucher')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('createNewVoucher')}
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Voucher Details */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Receipt size={20} color="#1976d2" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('voucherDetails')}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* Voucher Type */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>{t('voucherType')}</InputLabel>
                      <Select
                        value={formData.voucher_type}
                        label={t('voucherType')}
                        onChange={handleChange('voucher_type')}
                      >
                        <MenuItem value="receipt">{t('voucherTypes.receipt')}</MenuItem>
                        <MenuItem value="payment">{t('voucherTypes.payment')}</MenuItem>
                        <MenuItem value="journal">{t('voucherTypes.journal')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Voucher Number */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label={t('voucherNumber')}
                      value={formData.voucher_number}
                      onChange={handleChange('voucher_number')}
                      error={!!errors.voucher_number}
                      helperText={errors.voucher_number}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>

                  {/* Amount */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label={t('amount')}
                      value={formData.amount}
                      onChange={handleChange('amount')}
                      error={!!errors.amount}
                      helperText={errors.amount}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">{formData.currency}</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  {/* Status */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t('status')}</InputLabel>
                      <Select
                        value={formData.status}
                        label={t('status')}
                        onChange={handleChange('status')}
                      >
                        <MenuItem value="draft">{t('voucherStatuses.draft')}</MenuItem>
                        <MenuItem value="posted">{t('voucherStatuses.posted')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* VAT Settings */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includeVat}
                            onChange={(e) => setIncludeVat(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={t('includeVat', 'Include VAT')}
                      />
                      <Chip
                        label={includeVat ? t('ksaVatCompliant', 'KSA VAT Compliant') : t('vatExempt', 'VAT Exempt')}
                        color={includeVat ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Grid>

                  {includeVat && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label={t('vatRate', 'VAT Rate')}
                        value={vatRate}
                        onChange={(e) => setVatRate(Number(e.target.value))}
                      >
                        <MenuItem value={0}>{t('vatRates.0', '0% (Exempt)')}</MenuItem>
                        <MenuItem value={5}>{t('vatRates.5', '5%')}</MenuItem>
                        <MenuItem value={15}>{t('vatRates.15', '15% (Standard)')}</MenuItem>
                      </TextField>
                    </Grid>
                  )}

                  {/* VAT Summary */}
                  {baseAmount > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{
                        bgcolor: 'grey.50',
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('subtotal', 'Subtotal')}:
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(baseAmount)}
                          </Typography>
                        </Box>
                        {includeVat && vatRate > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('vatAmount', 'VAT')} ({vatRate}%):
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(vatAmount)}
                            </Typography>
                          </Box>
                        )}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {t('grandTotal', 'Grand Total')}:
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatCurrency(totalAmount)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Description */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={4}
                      label={t('description')}
                      value={formData.description}
                      onChange={handleChange('description')}
                      error={!!errors.description}
                      helperText={errors.description || `${formData.description.length}/500 ${t('common:characters')}`}
                      placeholder={t('enterVoucherDescription')}
                      inputProps={{ maxLength: 500 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Submit Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard/finance/vouchers')}
                disabled={loading}
              >
                {t('common:cancel')}
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                startIcon={<Save size={20} />}
              >
                {loading ? t('common:saving') : t('saveVoucher')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
